import {
  FormFieldsData, LoadingAnimation,
  ShowContext,
  SimpleBlock, Text,
  useListContext,
  VisibilityState
} from 'ui-builder';
import React, { ReactElement } from 'react';
import { ViewStyle } from 'react-native';
import { AppSpacings } from 'ui-library-mobile-theme/commonStyles';
import { get } from 'lodash';
import { CollapsibleDependentTasks } from './CollapsibleDependentTasks';

export interface RowRepeaterProps<DataType = FormFieldsData> {
  children: ReactElement,
  blockClassName?: string,
  groupClassName?: string | ((data: DataType) => string),
  loadMoreButtonClassName?: string,
  idField?: string,
  parentIdField?: string
  noDataMessage?: string
  getKeyFunc?: (row: FormFieldsData) => string;
  testId?: string
  style?: ViewStyle,
  defaultDependentTasksExpanded?: boolean,
}

export interface RowRepeaterLayoutProps extends RowRepeaterProps {
  data: FormFieldsData[];
  level: number;
  isLoading?: boolean;
  parentId?: number;
  getKeyFunc?: (row: FormFieldsData) => string;
  style?: ViewStyle
}

interface RowRendererProps {
  rowData: FormFieldsData;
  index: number;
  props: RowRepeaterLayoutProps;
  idField: string;
  visibleItemNumber: number;
  getGroupClassName: (item: FormFieldsData) => string | undefined;
  childrenMap: Map<number, FormFieldsData[]>;
}

function buildChildrenMap(
  data: FormFieldsData[],
  parentIdField: string,
): Map<number, FormFieldsData[]> {
  const map = new Map<number, FormFieldsData[]>();

  for (const item of data) {
    const parentId = get(item, parentIdField);
    if (parentId !== undefined && parentId !== null) {
      const children = map.get(parentId) || [];
      children.push(item);
      map.set(parentId, children);
    }
  }

  return map;
}

function countDependentTasksRecursively(
  parentId: number,
  childrenMap: Map<number, FormFieldsData[]>,
): number {
  const directChildren = childrenMap.get(parentId) || [];

  let count = directChildren.length;
  for (const child of directChildren) {
    if (childrenMap.has(child.id)) {
      count += countDependentTasksRecursively(child.id, childrenMap);
    }
  }

  return count;
}

function RowChildrenRenderer(rendererProps: {
  rowData: FormFieldsData;
  props: RowRepeaterLayoutProps;
  idField: string;
  childrenMap: Map<number, FormFieldsData[]>;
}) {
  const { rowData, props, idField, childrenMap } = rendererProps;

  if (!props.parentIdField) {
    return null;
  }

  const dependentTasksCount = childrenMap.has(rowData.id)
    ? countDependentTasksRecursively(rowData.id, childrenMap)
    : 0;

  const childrenElement = (
    <RowRepeaterLayout
      {...props}
      parentId={rowData.id}
      key={`row-${rowData[idField]}-children`}
      level={props.level + 1}
      style={props.style}
    >
      {props.children}
    </RowRepeaterLayout>
  );

  const isFirstLevel = props.level === 0;
  const shouldWrapInCollapsible = isFirstLevel && dependentTasksCount > 0;

  if (shouldWrapInCollapsible) {
    return (
      <CollapsibleDependentTasks
        dependentTasksCount={dependentTasksCount}
        defaultExpanded={props.defaultDependentTasksExpanded}
      >
        {childrenElement}
      </CollapsibleDependentTasks>
    );
  } else {
    return childrenElement;
  }
}

function RowRenderer(rendererProps: RowRendererProps) {
  const {
    rowData,
    index,
    props,
    idField,
    visibleItemNumber,
    getGroupClassName,
    childrenMap,
  } = rendererProps;

  const isEvenRow = visibleItemNumber % 2 === 0 && props.level === 0;
  const groupClassName = getGroupClassName(rowData) || '';
  const evenClassName = isEvenRow ? 'even' : '';

  return (
    <SimpleBlock
      className={`${groupClassName} ${evenClassName}`}
      style={props.style}
    >
      <ShowContext
        data={{
          ...rowData,
          __index: index,
        }}
      >
        {React.cloneElement(props.children)}
      </ShowContext>
      <RowChildrenRenderer
        rowData={rowData}
        props={props}
        idField={idField}
        childrenMap={childrenMap}
      />
    </SimpleBlock>
  );
}

function RowRepeaterLayout(props: RowRepeaterLayoutProps) {
  const idField = props.idField || 'id';
  const listContext = useListContext();

  const data = getFilteredData(props);

  const childrenMap = props.parentIdField
    ? buildChildrenMap(props.data, props.parentIdField)
    : new Map<number, FormFieldsData[]>();

  function getGroupClassName(item: FormFieldsData) {
    if (typeof props.groupClassName === 'function') {
      return props.groupClassName(item);
    }
    return props.groupClassName;
  }

  if (data.length === 0) {
    if (props.isLoading) {
      return <LoadingAnimation />;
    }
    return null;
  }

  let visibleItemNumber = 0;

  const rows = data.map((rowData: FormFieldsData, index) => {
    const visibilityState = getVisibilityState(rowData, listContext);

    if (visibilityState === VisibilityState.HIDDEN) {
      return null;
    }

    visibleItemNumber += 1;
    const key = props.getKeyFunc
      ? props.getKeyFunc(rowData)
      : `row-${get(rowData, idField)}`;

    return (
      <RowRenderer
        key={key}
        rowData={rowData}
        index={index}
        props={props}
        idField={idField}
        visibleItemNumber={visibleItemNumber}
        getGroupClassName={getGroupClassName}
        childrenMap={childrenMap}
      />
    );
  });

  const blockClassName = [
    props.blockClassName || '',
    props.isLoading ? 'loading' : '',
    `level${props.level}`,
  ].filter(Boolean).join(' ');

  return (
    <SimpleBlock
      testID={props.level === 0 ? props.testId : undefined}
      className={blockClassName}
      style={props.style}
    >
      {rows}
    </SimpleBlock>
  );
}

function getFilteredData(props: RowRepeaterLayoutProps): FormFieldsData[] {
  if (!props.parentIdField) {
    return props.data || [];
  }

  if (!props.data) {
    return [];
  }

  return props.data.filter((item: FormFieldsData) => {
    if (props.parentId) {
      return item?.parentTaskRelation?.parentTaskId === props.parentId;
    }
    return !item?.parentTaskRelation?.parentTaskId;
  });
}

function getVisibilityState(
  rowData: FormFieldsData,
  listContext: ReturnType<typeof useListContext>,
): VisibilityState {
  if ('id' in rowData) {
    return listContext.getVisibilityState!(rowData.id);
  }
  return VisibilityState.VISIBLE;
}

export function RowRepeater(props: RowRepeaterProps) {
  const listContext = useListContext();
  const data = listContext.data || [];

  if (listContext.isLoading || listContext.hasVisibleItems!()) {
    return (
      <RowRepeaterLayout
        testId={props.testId}
        data={data}
        level={0}
        isLoading={listContext.isLoading}
        parentIdField={props.parentIdField}
        idField={props.idField}
        blockClassName={props.blockClassName}
        groupClassName={props.groupClassName}
        loadMoreButtonClassName={props.loadMoreButtonClassName}
        getKeyFunc={props.getKeyFunc}
        style={props.style}
        defaultDependentTasksExpanded={props.defaultDependentTasksExpanded}
      >
        {props.children}
      </RowRepeaterLayout>
    );
  }

  return (
    <SimpleBlock
      className="no-messages"
      style={{
        paddingHorizontal: AppSpacings.pageHorizontalPadding,
        paddingVertical: AppSpacings.pageVerticalPadding,
      }}
    >
      <Text>{props.noDataMessage}</Text>
    </SimpleBlock>
  );
}
